// Store-and-forward queue worker. Runs every heartbeat interval, drains in batches.

import { api } from '../api.js';
import { config } from '../config.js';
import { state, notify } from '../state.js';
import { queueGetAll, queueRemove, queueLength } from '../storage.js';

let timer = null;
let inFlight = false;

export async function startQueueWorker() {
  stopQueueWorker();
  await flush();
  schedule();
}

export function stopQueueWorker() {
  if (timer) clearTimeout(timer);
  timer = null;
}

function schedule() {
  const ms = (config.heartbeatSeconds || 60) * 1000;
  timer = setTimeout(async () => {
    await flush();
    schedule();
  }, ms);
}

export async function flush() {
  if (inFlight) return;
  if (!navigator.onLine) {
    state.pendingQueueCount = await queueLength();
    notify();
    return;
  }

  inFlight = true;
  try {
    const all = await queueGetAll();
    if (!all.length) {
      state.pendingQueueCount = 0;
      notify();
      return;
    }

    const batchSize = Math.max(1, config.batchSize || 10);
    // Drain in batches; stop on first failure to preserve FIFO ordering.
    while (true) {
      const current = await queueGetAll();
      if (!current.length) break;
      const batch = current.slice(0, batchSize);
      try {
        const res = await api.marksBatch({ items: batch });
        if (!res?.success) {
          console.warn('marks batch rejected', res?.message);
          break;
        }
        const accepted = (res.result?.results || [])
          .filter((r) => r.accepted)
          .map((r) => r.clientUuid);
        await queueRemove(accepted);
        // If some items were not accepted (e.g. unknown employee), drop them too with warning logged.
        const dropped = (res.result?.results || [])
          .filter((r) => !r.accepted)
          .map((r) => r.clientUuid);
        if (dropped.length) {
          await queueRemove(dropped);
          console.warn('marks dropped (will not retry):', dropped);
        }
      } catch (err) {
        console.debug('marks batch error (will retry)', err);
        break;
      }
    }

    state.pendingQueueCount = await queueLength();
    notify();
  } finally {
    inFlight = false;
  }
}
